import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { HighlightTemplate, SlideLayoutProps } from '../types';
import {
  BodyContent,
  LEADING,
  PADDING,
  SlideText,
  TYPE,
  fitTitle,
  formatPageLabel,
  hasMath,
  hasNextSlide,
  shouldShowCounter,
  splitByWords,
  splitLastWord,
} from '../_shared';

// "Editorial Cream" - minimal cream surface, lime highlight on the
// title's last word for cover, framed by double rules. Counter badge
// bottom-right on body slides; the page badge gains a small arrow when
// there is a next slide.

const SURFACE = 'bg-[#F2EFE6]';
const HIGHLIGHT = 'bg-[#DFFC4B]';
const INK = 'text-black';
const RULE = 'bg-black/80';

function DoubleRule() {
  return (
    <div className='flex flex-col gap-[10px]'>
      <div className={cn('h-px w-full', RULE)} />
      <div className={cn('h-px w-full', RULE)} />
    </div>
  );
}

// The title with its last word in the highlight box. A title with maths
// renders whole: a split could cut through the expression.
function LastWordHighlight({
  title,
  boxClass,
}: {
  title: string;
  boxClass: string;
}) {
  if (hasMath(title)) return <SlideText text={title} />;
  const { lead, tail } = splitLastWord(title);
  return (
    <>
      {lead && <>{lead} </>}
      <span className={cn(boxClass, HIGHLIGHT)}>{tail}</span>
    </>
  );
}

function CoverLayout({ card }: SlideLayoutProps) {
  return (
    <div className={cn('relative flex h-full w-full flex-col', SURFACE)}>
      <div className={cn(PADDING.slideX, PADDING.slideTop)}>
        <DoubleRule />
      </div>

      <div
        className={cn(
          'flex flex-1 flex-col justify-center pb-10',
          PADDING.slideX
        )}
      >
        <h1
          className={cn(
            'text-balance font-semibold !leading-[1.15] tracking-tight',
            INK,
            fitTitle(card.title)
          )}
        >
          {/* inline-block so the highlighted word has its own box - no
              box-decoration-clone descender clipping. */}
          <LastWordHighlight
            title={card.title}
            boxClass='inline-block px-2 pb-2 pt-1 leading-[0.9]'
          />
        </h1>
        {card.body && (
          <p
            className={cn(
              'mt-8 max-w-md text-balance font-medium capitalize tracking-[0.08em]',
              INK,
              TYPE.coverSubtitle,
              LEADING.caption
            )}
          >
            <SlideText text={card.body} />
          </p>
        )}
      </div>
    </div>
  );
}

function ContentLayout({ card, slideIndex, totalSlides }: SlideLayoutProps) {
  const math = hasMath(card.title);
  const showCounter = shouldShowCounter(slideIndex, totalSlides);
  const showArrow = hasNextSlide(slideIndex, totalSlides);
  const counterLabel = showCounter
    ? formatPageLabel(slideIndex, totalSlides)
    : null;

  return (
    <div className={cn('relative flex h-full w-full flex-col', SURFACE)}>
      <div className={cn(PADDING.slideX, PADDING.slideTop)}>
        <DoubleRule />
      </div>

      <div
        className={cn(
          'flex flex-1 flex-col items-center justify-center text-center',
          PADDING.slideX
        )}
      >
        {/* Per-word highlight boxes: each word is its own block, so wrapping
            can never clip a descender or overlap the line above (which the
            single box-decoration-clone span did). */}
        <h2
          className={cn(
            'mb-8 flex flex-wrap justify-center gap-x-2 gap-y-3 font-semibold tracking-tight',
            INK,
            fitTitle(card.title, 'body')
          )}
        >
          {/* A title with maths is one box: splitting could cut through the
              expression. */}
          {(math ? [card.title] : splitByWords(card.title)).map((word, i) => (
            <span
              key={i}
              className={cn('px-2 pb-2 pt-1 leading-[0.9]', HIGHLIGHT)}
            >
              {math ? <SlideText text={word} /> : word}
            </span>
          ))}
        </h2>
        {card.body && (
          <BodyContent
            body={card.body}
            visual={card.visual}
            className={cn('text-pretty text-center', INK)}
            spacing='space-y-5'
          />
        )}
      </div>

      <div className={cn('relative pb-24', PADDING.slideX)}>
        <div className={cn('h-px w-full', RULE)} />
        {(counterLabel || showArrow) && (
          <div
            className={cn(
              'absolute bottom-8 right-8 flex h-12 items-center gap-1.5 rounded-full border border-black/70 px-3 font-medium md:right-12',
              HIGHLIGHT,
              INK,
              TYPE.page
            )}
          >
            {counterLabel && <span>{counterLabel}</span>}
            {showArrow && (
              <ArrowRight className='h-3.5 w-3.5' strokeWidth={2.5} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ThumbnailLayout({ card }: SlideLayoutProps) {
  return (
    <div className={cn('flex h-full w-full flex-col p-5', SURFACE)}>
      <div className='flex flex-col gap-[5px]'>
        <div className={cn('h-px w-full', RULE)} />
        <div className={cn('h-px w-full', RULE)} />
      </div>

      <div className='flex flex-1 flex-col justify-center'>
        <h3
          className={cn(
            'line-clamp-3 text-balance font-semibold tracking-tight',
            INK,
            fitTitle(card.title, 'thumb'),
            LEADING.display
          )}
        >
          <LastWordHighlight
            title={card.title}
            boxClass='box-decoration-clone px-1.5 py-0.5'
          />
        </h3>
        {card.body && (
          <p
            className={cn(
              'mt-3 line-clamp-2 font-medium capitalize tracking-[0.05em]',
              INK,
              TYPE.caption,
              LEADING.caption
            )}
          >
            <SlideText text={card.body} />
          </p>
        )}
      </div>
    </div>
  );
}

export const Template02: HighlightTemplate = {
  id: 'template-02',
  name: 'Editorial Cream',
  layouts: {
    cover: CoverLayout,
    body: ContentLayout,
    summary: ContentLayout,
  },
  thumbnail: ThumbnailLayout,
};
